import type { InboxMailboxUser } from '@/types/senderEmail'

/**
 * The person filter on the Inbox, and what survives a mailbox switch.
 *
 * Switching mailbox used to throw the person selection away and snap back to
 * "My replies" every time. On a shared mailbox that is the wrong default and a
 * jarring one: you pick a teammate, change which mailbox you are reading, and
 * the answer you were part way through reading is replaced by a different one
 * you did not ask for.
 *
 * It cannot simply be carried over either. The people list belongs to the
 * mailbox, so a teammate who has mail on one mailbox may have none on the next,
 * and keeping their id would ask the API for a combination that does not exist.
 * Hence: keep the selection when it still means something, fall back when it
 * does not.
 */

/** Buckets that exist on every mailbox, so they always survive a switch.
 *  'me' and 'all-members' are about the viewer, not the mailbox, and every
 *  mailbox has an unassigned bucket even when it is empty. */
export const MAILBOX_INDEPENDENT_PEOPLE = ['me', 'unassigned', 'all-members'] as const

/**
 * The mailbox selector's value for "every mailbox I can read", merged into one
 * list newest first.
 *
 * A sentinel rather than the empty string, which the selector already uses to
 * mean "no mailbox resolved yet" and which would otherwise make "still booting"
 * and "deliberately merged" indistinguishable. The API expresses the same thing
 * by omitting sender_email_id, so this value never leaves the client.
 */
export const ALL_MAILBOXES = 'all'

/** The mailbox id to query for a selector value, or undefined for the merged
 *  view. One place, so the list and the people roster cannot disagree about
 *  which mailbox is being asked for. */
export function mailboxIdFor(value: string): number | undefined {
  if (!value || value === ALL_MAILBOXES) return undefined
  const id = Number(value)
  // A whole positive number or nothing. `Number.isFinite` alone lets '1.5e-9'
  // through as a mailbox id, which is not one: the API would 404 on it and the
  // list would fall back to the merged view without saying why.
  return Number.isInteger(id) && id > 0 ? id : undefined
}

export type PersonQuery = {
  contact_user_id: number | undefined
  unassigned: boolean
  all_members: boolean
}

/**
 * The person filter to use after moving to a mailbox whose roster is `roster`.
 *
 * `fallback` is the caller's role-dependent default ("My replies", or the
 * merged view for a read-only role attributed no mail of its own).
 *
 * Permissions are not re-checked here: they come from the viewer's role, not
 * from the mailbox, so a value that was offered before the switch is still
 * allowed after it. Only the roster can change.
 */
export function personFilterForMailbox(
  current: string,
  roster: InboxMailboxUser[],
  fallback: string,
): string {
  if ((MAILBOX_INDEPENDENT_PEOPLE as readonly string[]).includes(current)) return current

  const id = Number(current)
  if (!Number.isFinite(id)) return fallback
  // `is_self` is excluded deliberately: the viewer's own row is selected as
  // 'me', never by id, so an id that happens to match it is not a valid value
  // and would leave the dropdown with nothing highlighted.
  const stillOnThisMailbox = roster.some((u) => !u.is_self && u.user_id === id)
  return stillOnThisMailbox ? current : fallback
}

/**
 * The query a person filter means, in one place.
 *
 * Both handlers that change what is listed — picking a person, and picking a
 * mailbox while keeping the person — go through this, so a mailbox switch
 * cannot fetch something subtly different from what selecting the same person
 * directly would have fetched.
 */
export function personQuery(person: string): PersonQuery {
  return {
    contact_user_id:
      (MAILBOX_INDEPENDENT_PEOPLE as readonly string[]).includes(person)
        ? undefined
        : Number(person),
    unassigned: person === 'unassigned',
    all_members: person === 'all-members',
  }
}

/**
 * The order the people dropdown presents its buckets in.
 *
 * Broadest first: "All members" merges everyone this role may read, so it is
 * read before narrowing to one person, and it lines up with "All mailboxes"
 * sitting first in the selector beside it. Then you, then individual
 * teammates. "Unassigned" is last -- it is a triage bucket rather than a
 * person, and wedged between colleagues it read as one of them.
 *
 * A function rather than four spreads at the call site so the order is one
 * statement that can be asserted, instead of something a later edit can
 * quietly shuffle. Any group may be empty (a Rep has no "All members", a
 * mailbox may have no teammates).
 */
export function orderPersonOptions<T>(groups: {
  allMembers: T[]
  self: T[]
  teammates: T[]
  unassigned: T[]
}): T[] {
  return [...groups.allMembers, ...groups.self, ...groups.teammates, ...groups.unassigned]
}
